import { useState } from 'react'
import { Cake, CheckCircle2, Eye, Mail, Send, TriangleAlert } from 'lucide-react'
import { foutTekst, vraag } from '../lib/api'
import { datumTijd, getal } from '../lib/opmaak'
import { Lade } from '../onderdelen/Lade'
import { Laden, useLaad } from './Beheer'

/*
 * Marketing: de welkomstmail bij registratie, de verjaardagsmail en de
 * verjaardagspunten. De teksten zijn platte tekst; de opmaak (kop, kleuren,
 * afmeldlink) doet de server. {naam} {code} {saldo} {punten} {link} worden
 * per klant ingevuld.
 */

interface Sjabloon { aan: boolean; onderwerp: string; tekst: string }
interface Data {
  sjablonen: { welkomst: Sjabloon; verjaardag: Sjabloon; terug: Sjabloon & { dagen: number } }
  zonderReview: number
  verjaardag: { aan: boolean; punten: number; mail: boolean }
  magVerjaardagInstellen: boolean
  afzenderIngesteld: boolean
  afzender: string | null
  stand: Record<string, number>
  recent: { id: string; soort: string; aan: string; onderwerp: string; status: string; t: string; verzonden: string | null; fout: string | null }[]
}

const STATUS: Record<string, { tekst: string; klasse: string }> = {
  verzonden: { tekst: 'Verzonden', klasse: 'open' }, wacht: { tekst: 'Wacht', klasse: 'let' },
  bezig: { tekst: 'Bezig', klasse: 'let' }, fout: { tekst: 'Mislukt', klasse: 'dicht' },
}
const SOORT: Record<string, string> = { welkomst: 'Welkomst', verjaardag: 'Verjaardag', terug: 'Welkom terug', test: 'Test' }

export function Marketing() {
  const { data, fout, laad, zet } = useLaad<Data>('mails')
  const [melding, zetMelding] = useState<string | null>(null)
  if (!data) return <><div className="pagina-kop"><h2>Marketing</h2></div><Laden fout={fout} opnieuw={laad} /></>

  return (
    <>
      <div className="pagina-kop"><h2>Marketing</h2></div>
      <div className="lijst" style={{ maxWidth: 820, gap: 16 }}>
        {data.afzenderIngesteld
          ? <div className="melding ok"><CheckCircle2 size={19} /><span>Mails gaan uit via <b>{data.afzender ?? 'het ingestelde adres'}</b>. {data.stand.wacht ? `${data.stand.wacht} in de wachtrij.` : 'De wachtrij is leeg.'}</span></div>
          : <div className="melding let"><TriangleAlert size={19} /><span>Er is nog geen afzender ingesteld; mails blijven in de wachtrij tot dat er is.</span></div>}
        {melding && <div className="melding info">{melding}</div>}

        <Verjaardag data={data} zet={zet} />
        <SjabloonKaart soort="welkomst" titel="Welkomstmail" uitleg="Bij het registreren van een nieuwe kaart op de tablet. Gaat naar iedereen die registreert (het is een bevestiging)."
          s={data.sjablonen.welkomst} zet={zet} melding={zetMelding} kanTesten={data.afzenderIngesteld} />
        <SjabloonKaart soort="verjaardag" titel="Verjaardagsmail" uitleg="Op de verjaardag, samen met de verjaardagspunten. Alleen naar klanten die toestemming gaven; altijd met een afmeldlink."
          s={data.sjablonen.verjaardag} zet={zet} melding={zetMelding} kanTesten={data.afzenderIngesteld} />
        <SjabloonKaart soort="terug" titel="Welkom terug" s={data.sjablonen.terug} zet={zet} melding={zetMelding} kanTesten={data.afzenderIngesteld}
          uitleg={`Voor wie een tijd niet gewassen heeft: twee uur na de wasbeurt, met de Google-reviewlink van die vestiging. Alleen met toestemming, hooguit één keer per 30 dagen.${data.zonderReview ? ` Let op: ${data.zonderReview} ${data.zonderReview === 1 ? 'vestiging heeft' : 'vestigingen hebben'} nog geen reviewlink (Beheer → Vestigingen); daar gaat nog geen mail uit.` : ''}`} />

        <section className="kaart vol">
          <div className="sectie-kop" style={{ marginTop: 0 }}>
            <h2>Laatste mails</h2>
            {!!data.stand.wacht && data.afzenderIngesteld && (
              <button className="knop klein-knop" onClick={async () => { try { zet(await vraag('mails-versturen')) } catch (e) { zetMelding(foutTekst(e)) } }}>
                <Send size={16} />Nu versturen
              </button>
            )}
          </div>
          <p className="stil klein" style={{ marginBottom: 8 }}>
            {['verzonden', 'wacht', 'fout'].map((k) => `${getal(data.stand[k] ?? 0)} ${STATUS[k]?.tekst.toLowerCase()}`).join(' · ')}
          </p>
          {data.recent.length ? (
            <ul className="tijdlijn">
              {data.recent.map((m) => (
                <li key={m.id} style={{ alignItems: 'flex-start' }}>
                  <Mail size={18} className="stil" style={{ marginTop: 3 }} />
                  <span className="vul" style={{ minWidth: 0 }}>
                    <b className="afkap" style={{ display: 'block' }}>{m.onderwerp}</b>
                    <span className="stil klein">{SOORT[m.soort] ?? m.soort} · {m.aan} · {datumTijd(m.verzonden ?? m.t)}</span>
                    {m.fout && m.status !== 'verzonden' && <span className="klein" style={{ display: 'block', color: 'var(--text-danger)' }}>{m.fout}</span>}
                  </span>
                  <span className={`badge ${STATUS[m.status]?.klasse ?? ''}`}>{STATUS[m.status]?.tekst ?? m.status}</span>
                </li>
              ))}
            </ul>
          ) : <p className="stil">Nog geen mails.</p>}
        </section>
      </div>
    </>
  )
}

function Verjaardag({ data, zet }: { data: Data; zet: (d: Data) => void }) {
  const [aan, zetAan] = useState(data.verjaardag.aan)
  const [punten, zetPunten] = useState(String(data.verjaardag.punten || ''))
  const [mail, zetMail] = useState(data.verjaardag.mail)
  const [uitslag, zetUitslag] = useState<string | null>(null)
  const mag = data.magVerjaardagInstellen
  return (
    <section className="kaart vol formulier">
      <div className="rij" style={{ flexWrap: 'nowrap' }}>
        <span className="beloning-icoon"><Cake size={22} /></span>
        <div className="vul"><b>Verjaardagspunten</b><p className="stil klein">Klanten vullen hun verjaardag (dag en maand) zelf in bij Mijn gegevens. Om 08:00 op hun verjaardag komen de punten erbij. Hooguit één keer per jaar; een nieuwe of gewijzigde datum telt pas na 30 dagen.</p></div>
      </div>
      <label className="vink"><input type="checkbox" checked={aan} disabled={!mag} onChange={(e) => zetAan(e.target.checked)} /><span>Punten geven op de verjaardag</span></label>
      {aan && (
        <div className="twee-kol">
          <label className="veld"><span>Hoeveel punten</span><input value={punten} disabled={!mag} inputMode="numeric" onChange={(e) => zetPunten(e.target.value.replace(/\D/g, ''))} placeholder="bijv. 5" /></label>
          <label className="vink" style={{ alignSelf: 'end' }}><input type="checkbox" checked={mail} disabled={!mag} onChange={(e) => zetMail(e.target.checked)} /><span>Met felicitatiemail</span></label>
        </div>
      )}
      {uitslag && <div className="melding info">{uitslag}</div>}
      {mag
        ? <div className="knoppenrij"><button className="knop geel" disabled={aan === data.verjaardag.aan && Number(punten || 0) === data.verjaardag.punten && mail === data.verjaardag.mail} onClick={async () => {
          try {
            await vraag('instellingen-opslaan', { verjaardag: { aan, punten: Number(punten || 0), mail } })
            zet(await vraag('mails')); zetUitslag('Opgeslagen.')
          } catch (e) { zetUitslag(foutTekst(e)) }
        }}>Opslaan</button></div>
        : <p className="stil klein">Alleen MT kan dit wijzigen.</p>}
    </section>
  )
}


function SjabloonKaart({ soort, titel, uitleg, s, zet, melding, kanTesten }: {
  soort: 'welkomst' | 'verjaardag' | 'terug'; titel: string; uitleg: string; s: Sjabloon & { dagen?: number }; zet: (d: Data) => void; melding: (m: string) => void; kanTesten: boolean
}) {
  const [dagen, zetDagen] = useState(String(s.dagen ?? ''))
  const [aan, zetAan] = useState(s.aan)
  const [onderwerp, zetOnderwerp] = useState(s.onderwerp)
  const [tekst, zetTekst] = useState(s.tekst)
  const [bezig, zetBezig] = useState(false)
  const [fout, zetFout] = useState<string | null>(null)
  const gewijzigd = aan !== s.aan || onderwerp !== s.onderwerp || tekst !== s.tekst || (s.dagen !== undefined && Number(dagen) !== s.dagen)
  const extra = s.dagen !== undefined ? { dagen: Number(dagen) } : {}
  const [voorbeeld, zetVoorbeeld] = useState<{ onderwerp: string; html: string } | null>(null)
  const bekijk = async () => {
    zetFout(null)
    try { zetVoorbeeld(await vraag<{ onderwerp: string; html: string }>('mail-voorbeeld', { soort, onderwerp, tekst })) } catch (e) { zetFout(foutTekst(e)) }
  }

  const opslaan = async () => {
    zetBezig(true); zetFout(null)
    try { zet(await vraag('mail-sjabloon-opslaan', { soort, aan, onderwerp, tekst, ...extra })); melding(`${titel} opgeslagen.`) } catch (e) { zetFout(foutTekst(e)) } finally { zetBezig(false) }
  }
  const test = async () => {
    zetBezig(true); zetFout(null)
    try {
      if (gewijzigd) zet(await vraag('mail-sjabloon-opslaan', { soort, aan, onderwerp, tekst, ...extra }))
      const r = await vraag<{ aan: string }>('mail-test', { soort })
      melding(`Testmail verstuurd naar ${r.aan}.`)
      zet(await vraag('mails'))
    } catch (e) { zetFout(foutTekst(e)) } finally { zetBezig(false) }
  }

  return (
    <section className="kaart vol formulier">
      <div><b>{titel}</b><p className="stil klein">{uitleg}</p></div>
      <label className="vink"><input type="checkbox" checked={aan} onChange={(e) => zetAan(e.target.checked)} /><span>Deze mail versturen</span></label>
      {s.dagen !== undefined && (
        <label className="veld"><span>Na hoeveel dagen zonder wasbeurt</span>
          <span className="rij" style={{ flexWrap: 'nowrap' }}>
            <input value={dagen} inputMode="numeric" style={{ maxWidth: 110 }} onChange={(e) => zetDagen(e.target.value.replace(/\D/g, '').slice(0, 3))} />
            <span className="stil">dagen{Number(dagen) >= 28 ? ` (± ${Math.round(Number(dagen) / 30)} ${Math.round(Number(dagen) / 30) === 1 ? 'maand' : 'maanden'})` : ''}</span>
          </span>
          <small>Standaard 90 dagen (3 maanden). Van 14 tot 730 dagen.</small>
        </label>
      )}
      <label className="veld"><span>Onderwerp</span><input value={onderwerp} onChange={(e) => zetOnderwerp(e.target.value)} maxLength={150} /></label>
      <label className="veld"><span>Tekst</span>
        <textarea value={tekst} onChange={(e) => zetTekst(e.target.value)} rows={9} maxLength={4000} style={{ resize: 'vertical', lineHeight: 1.5 }} />
        <small>Vult zichzelf in: <code>{'{naam}'}</code> <code>{'{code}'}</code> <code>{'{saldo}'}</code> <code>{'{punten}'}</code> <code>{'{link}'}</code>{soort === 'terug' && <> <code>{'{vestiging}'}</code></>}. Een witregel = nieuwe alinea.</small>
      </label>
      <Lade titel={voorbeeld ? voorbeeld.onderwerp : 'Voorbeeld'} open={!!voorbeeld} sluit={() => zetVoorbeeld(null)} breed kinderen={voorbeeld && (
        <div className="lijst" style={{ gap: 10 }}>
          <p className="stil klein">Zo ziet de klant hem, met voorbeeldgegevens (kaart C123456, 42 punten).</p>
          <iframe className="mail-voorbeeld" title="Voorbeeld van de mail" sandbox="allow-same-origin" srcDoc={voorbeeld.html}
            onLoad={(e) => { const d = e.currentTarget.contentDocument; if (d) e.currentTarget.style.height = `${d.documentElement.scrollHeight + 4}px` }} />
        </div>
      )} />
      {fout && <div className="melding fout">{fout}</div>}
      <div className="knoppenrij">
        <button className="knop" type="button" onClick={bekijk}><Eye size={17} />Voorbeeld</button>
        <button className="knop" disabled={bezig || !kanTesten} onClick={test} title={kanTesten ? '' : 'Nog geen afzender'}><Send size={17} />Testmail naar mij</button>
        <button className="knop geel" disabled={bezig || !gewijzigd} onClick={opslaan}>Opslaan</button>
      </div>
    </section>
  )
}
